import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { MyCarsDataPropTypes } from '../propTypes';
import { useIsChromeless } from '../hooks/useIsChromeless';
import ModalNav from './ModalNav';
import ModalCard from './ModalCard';

/**
 * Just the content itself -- ModalNav's shopping-activity links and
 * ModalCard's shop-our-inventory panel -- with none of the surrounding
 * popup frame (no close button, no box). Two different callers share this
 * one component so they can't drift apart: MyCarsModal wraps it in that
 * frame and traps keyboard focus inside the popup itself; Widget's
 * inline-content mode skips the frame and expects whatever it's embedded
 * in (like a drawer's own panel) to trap focus instead.
 */
const MyCarsContent = forwardRef(
	({ isMobile, myCarsData, welcome, onClickHandler }, ref) => {
		const isChromeless = useIsChromeless();
		const navRef = useRef(null);
		const cardRef = useRef(null);

		useImperativeHandle(ref, () => ({
			getLinkNodes: () => navRef.current?.getLinkNodes() ?? [],
			getShoppingActivityRef: () =>
				cardRef.current?.getShoppingActivityRef()
		}));

		const contentClasses = [
			'd-flex',
			'flex-wrap',
			isMobile ? 'flex-column px-4' : 'px-6',
			isChromeless ? 'mycars-content--chromeless' : ''
		];

		return (
			<div
				className={setClassNames(contentClasses)}
				data-testid="mycars-content"
			>
				<ModalNav
					ref={navRef}
					isMobile={isMobile}
					myCarsData={myCarsData}
					onClickHandler={onClickHandler}
				/>
				<ModalCard
					ref={cardRef}
					isMobile={isMobile}
					welcome={welcome}
					onClickHandler={onClickHandler}
				/>
			</div>
		);
	}
);

MyCarsContent.propTypes = {
	isMobile: PropTypes.bool.isRequired,
	welcome: PropTypes.bool.isRequired,
	onClickHandler: PropTypes.func.isRequired,
	myCarsData: MyCarsDataPropTypes
};

MyCarsContent.defaultProps = {
	myCarsData: undefined
};

export default MyCarsContent;
